import { useReactiveVar } from '@apollo/client'
import HoverTooltip from '@deltek/specification-client/core/Feedbacks/HoverTooltip'
import { workspaceSelectedElement } from 'api/graphQL/reactiveVars/element'
import { DataNode } from 'components/WorkspaceEditor/types'
import { useShallowSelector } from 'components/WorkspaceEditor/Workspace2Store'
import ThunderboltIcon from './ThunderboltIcon'
import { ProjectPageContext } from 'pages/ProjectsPage/ProjectPage/components/ProjectPageContext'
import { PROPERTY_EDITOR_EDITABLE_TEXT_VARIANT as NODE_TYPES } from 'pages/ProjectsPage/WorkspacePage/components/constants'
import React, { memo, useContext } from 'react'
import { shouldDisplayLevelOfSpecificationIcon } from '../hooks'
import { useLevelOfSpecificationStyles } from '../styles'

interface LevelOfSpecificationIconProps {
    node: DataNode
}

/**
 * Displays the Level of Specification thunderbolt icon when the item's LOS tag exceeds project/family LOS
 */
export const LevelOfSpecificationIcon = memo<LevelOfSpecificationIconProps>(
    ({ node }) => {
        const classes = useLevelOfSpecificationStyles()
        const projectPageContext = useContext(ProjectPageContext)

        if (!projectPageContext) {
            return null
        }

        const { projectData } = projectPageContext

        const {
            selectedProjectElementNode,
            isContentTeamFromStudioPage,
        } = useShallowSelector((state) => ({
            selectedProjectElementNode: state.selectedProjectElementNode,
            isContentTeamFromStudioPage: state.isContentTeamFromStudioPage,
        }))

        const { familyProjectMetadata } = useReactiveVar(
            workspaceSelectedElement
        )

        // Only show for Article nodes
        if (node.type !== NODE_TYPES.Article) {
            return null
        }

        const shouldDisplayLOSIcon = shouldDisplayLevelOfSpecificationIcon({
            node,
            isInProject: selectedProjectElementNode?.isInProject ?? false,
            projectLevelOfSpecification:
                projectData?.getProject?.levelOfSpecification,
            isTargetLevelOfSpecificationManuallyUpdated:
                familyProjectMetadata?.isTargetLevelOfSpecificationManuallyUpdated,
            targetLevelOfSpecification: familyProjectMetadata?.targetLOD,
            isContentTeamStudioView: isContentTeamFromStudioPage,
        })

        if (!shouldDisplayLOSIcon) {
            return null
        }

        const LOSTag = node.contentItemView?.levelOfSpecificationTag
        const tooltipText = `Activated at Level of Specification ${LOSTag}`
        const ariaLabel = `Level of Specification ${LOSTag}. This content is activated at a higher specification level than the current project setting.`

        return (
            <span
                className={classes.thunderBoltIconContainer}
                data-testid="los-icon"
                role="img"
                aria-label={ariaLabel}
                onMouseDown={(e) => {
                    e.stopPropagation()
                }}
            >
                <HoverTooltip text={tooltipText}>
                    <span
                        className={classes.thunderBoltIconButton}
                        aria-hidden="true"
                    >
                        <ThunderboltIcon />
                    </span>
                </HoverTooltip>
            </span>
        )
    }
)

LevelOfSpecificationIcon.displayName = 'LevelOfSpecificationIcon'
